import React, { useState, useEffect } from 'react';
import { Clock, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogPosts();
  }, []);

  if (loading) {
    return (
      <section id="blog" className="section-padding bg-transparent">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 size={40} className="animate-spin text-[#C4B5A0]" />
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section id="blog" className="section-padding bg-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-caption text-[#C4B5A0] mb-4">
            {t('blog.label')}
          </p>
          <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {t('blog.title')}
          </h2>
          <div className="divider" />
          <p className="text-body text-[#6B4E37]">
            {t('blog.subtitle')}
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <article
              key={post.id}
              className="card card-hover overflow-hidden group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="img-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#FAF9F7]/90 backdrop-blur-sm text-[0.7rem] tracking-widest uppercase text-[#6B4E37] rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta */}
                <div className="flex items-center gap-4 text-sm text-[#8B7D6B] mb-4">
                  <span>{post.date}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {post.readTime}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C] mb-3 group-hover:text-[#6B4E37] transition-colors">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-[#8B7D6B] mb-4 line-clamp-2">
                  {post.excerpt}
                </p>

                {/* Read More */}
                <button className="flex items-center gap-2 text-sm text-[#C4B5A0] hover:text-[#6B4E37] transition-colors group/btn">
                  Ler mais
                  <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
